// src/sockets/SocketContext.js

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { io } from 'socket.io-client';
import { notification } from 'antd';
import { getSocketIoUrl } from '../config';
import { setCsvStatistics, setDbActualDates, setSocketStatus, setProfile } from '../store/interfaceSlice';
import { normalizeProfileFromServer } from '../utils/helpers';
import { useTasksApi } from './hooks/useTasksApi';
import { useProfileApi } from './hooks/useProfileApi';
import { useReportApi } from './hooks/useReportApi';
import { useImageDescriptionApi } from './hooks/useImageDescriptionApi';
import { useGlobalSearchApi } from './hooks/useGlobalSearchApi';
import { useSubtaskUpdates } from './hooks/useSubtaskUpdates';

const SocketContext = createContext({
  socket: null,
  getTasksFromServer: () => {},
  createNewTaskOnServer: () => {},
  deleteTaskOnServer: () => {},
  getRequestsForTask: () => {},
  updateProfileOnServer: () => {},
  getImageDescription: () => {},
  createGlobalImageSearchTask: () => {},
  getGlobalSearchTaskList: () => {},
  getGlobalSearchTaskDetail: () => {},
});

export const useSocketContext = () => useContext(SocketContext);

export const SocketProvider = ({ token, children }) => {
  const dispatch = useDispatch();
  const selectedTaskIdRef = useRef(null);
  const reduxSelectedTaskId = useSelector((state) => state.tasks.selectedTask?.id);
  const hasBeenConnectedRef = useRef(false);

  useEffect(() => {
    selectedTaskIdRef.current = reduxSelectedTaskId;
  }, [reduxSelectedTaskId]);

  const socketUrl = useMemo(() => getSocketIoUrl(), []);

  const socket = useMemo(() => {
    if (!token) return null;
    return io(socketUrl, {
      transports: ['websocket'],
      auth: { token: `Bearer ${token}` },
      reconnectionAttempts: 10,
      reconnectionDelay: 5000,
      reconnectionDelayMax: 5000,
    });
  }, [token, socketUrl]);

  const tasksApi = useTasksApi(socket);
  const profileApi = useProfileApi(socket);
  const { getProfileFromServer } = profileApi;
  const reportApi = useReportApi(socket);
  const imageDescApi = useImageDescriptionApi(socket);
  const globalSearchApi = useGlobalSearchApi(socket);

  useSubtaskUpdates(socket);

  const handleConnect = useCallback(() => {
    const isReconnection = hasBeenConnectedRef.current;
    hasBeenConnectedRef.current = true;

    dispatch(setSocketStatus('connected'));
    getProfileFromServer();

    try {
      socket?.emit('get_db_actual_dates', {}, (response) => {
        if (response?.status === 'ok' && response?.dates && typeof response.dates === 'object') {
          dispatch(setDbActualDates(response.dates));
        }
      });
    } catch {
      /* ignore */
    }

    if (isReconnection && selectedTaskIdRef.current) {
      socket?.emit('get_requests', selectedTaskIdRef.current, () => {});
    }
  }, [dispatch, socket, getProfileFromServer]);

  const handleConnectError = useCallback(
    (err) => {
      if (!hasBeenConnectedRef.current) {
        notification.error({
          message: 'Ошибка подключения сокета',
          description: err.message,
        });
        dispatch(setSocketStatus('disconnected'));
      }
    },
    [dispatch]
  );

  const handleReconnectAttempt = useCallback(
    (attempt) => {
      notification.info({
        message: 'Переподключение сокета',
        description: `Попытка переподключения (${attempt})`,
      });
      dispatch(setSocketStatus('connecting'));
    },
    [dispatch]
  );

  useEffect(() => {
    if (!socket) {
      dispatch(setSocketStatus('disconnected'));
      return undefined;
    }

    dispatch(setSocketStatus('connecting'));

    socket.on('connect', handleConnect);
    socket.on('connect_error', handleConnectError);

    if (socket.io?.on) {
      socket.io.on('reconnect_attempt', handleReconnectAttempt);
      socket.io.on('reconnect_error', () => dispatch(setSocketStatus('connecting')));
      socket.io.on('reconnect_failed', () => {
        dispatch(setSocketStatus('disconnected'));
        notification.error({
          message: 'Не удалось переподключиться',
          description: 'Все попытки переподключения исчерпаны. Попробуйте перезагрузить страницу.',
        });
      });
    }

    socket.on('disconnect', (reason) => {
      if (socket.active) {
        dispatch(setSocketStatus('connecting'));
      } else {
        dispatch(setSocketStatus('disconnected'));
      }
      void reason;
    });

    socket.on('csv_statistics', (data) => {
      dispatch(setCsvStatistics(data));
    });

    socket.on('get_db_actual_dates', (payload) => {
      if (payload?.status === 'ok' && payload?.dates && typeof payload.dates === 'object') {
        dispatch(setDbActualDates(payload.dates));
      }
    });

    socket.on('profile_loaded', (data) => {
      try {
        if (data && typeof data === 'object') {
          dispatch(setProfile(normalizeProfileFromServer(data)));
        }
      } catch {
        /* ignore */
      }
    });

    socket.on('profile_updated', (data) => {
      try {
        const raw = data?.profile || data;
        if (raw && typeof raw === 'object') {
          dispatch(setProfile(normalizeProfileFromServer(raw)));
          notification.success({ message: 'Профиль обновлён' });
        }
      } catch {
        /* ignore */
      }
    });

    if (socket.connected) {
      handleConnect();
    }

    return () => {
      socket.off('connect', handleConnect);
      socket.off('connect_error', handleConnectError);
      if (socket.io?.off) {
        socket.io.off('reconnect_attempt', handleReconnectAttempt);
        socket.io.off('reconnect_error');
        socket.io.off('reconnect_failed');
      }
      socket.off('disconnect');
      socket.off('csv_statistics');
      socket.off('get_db_actual_dates');
      socket.off('profile_loaded');
      socket.off('profile_updated');
      hasBeenConnectedRef.current = false;
      if (socket.connected || socket.active) {
        socket.disconnect();
      } else {
        socket.close();
      }
    };
  }, [socket, handleConnect, handleConnectError, handleReconnectAttempt, dispatch]);

  useEffect(() => {
    if (!socket) return undefined;
    const handleBeforeUnload = () => {
      try {
        socket.close();
      } catch {
        /* ignore */
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [socket]);

  const contextValue = useMemo(
    () => ({
      socket,
      ...tasksApi,
      ...profileApi,
      ...reportApi,
      ...imageDescApi,
      ...globalSearchApi,
      getProfileFromServer,
    }),
    [socket, tasksApi, profileApi, reportApi, imageDescApi, globalSearchApi, getProfileFromServer]
  );

  return <SocketContext.Provider value={contextValue}>{children}</SocketContext.Provider>;
};

SocketProvider.propTypes = {
  token: PropTypes.string,
  children: PropTypes.node.isRequired,
};
